import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowUp,
  Menu,
  X,
  Instagram,
  Facebook,
  Mail,
  Send,
  Palette,
  Film,
  Handshake,
  Lightbulb,
  ExternalLink,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/rachel-creativeensemblemedia/30min";

/* ─── Animation constants (extracted to avoid inline object re-renders) ─── */
const FADE_INITIAL = { opacity: 0, y: 50 };
const FADE_VISIBLE = { opacity: 1, y: 0 };
const SIDEBAR_INITIAL = { x: "100%" };
const SIDEBAR_VISIBLE = { x: 0 };
const SIDEBAR_EXIT = { x: "100%" };
const SIDEBAR_TRANSITION = { type: "spring", damping: 30, stiffness: 300 };
const OVERLAY_INITIAL = { opacity: 0 };
const OVERLAY_VISIBLE = { opacity: 1 };
const OVERLAY_EXIT = { opacity: 0 };
const OVERLAY_TRANSITION = { duration: 0.3 };
const TESTIMONIAL_INITIAL = { opacity: 0, y: 25 };
const TESTIMONIAL_VISIBLE = { opacity: 1, y: 0 };
const TESTIMONIAL_TRANSITION = { duration: 0.5 };
const EMPTY_FORM = { name: "", email: "", message: "" };

const PORTFOLIO = [
  { title: "The Travel Edit", desc: "Curated guides, relocation resources, digital media, and brand partnerships. Bringing destinations to life through The Sweater Dork and The Aussie's Guide.", img: "https://img1.wsimg.com/isteam/getty/2114448180/:/cr=t:0%25,l:10.1%25,w:79.8%25,h:100%25/rs=w:500", headerBg: "#000", headerText: "#FC4BAF" },
  { title: "She Speaks", desc: "Empowering women through podcasts, articles, visual art, and digital experiences. Meaningful conversations that celebrate creativity.", img: "https://img1.wsimg.com/isteam/getty/1681036303/:/cr=t:0%25,l:5.75%25,w:88.5%25,h:100%25/rs=w:500", headerBg: "#FC4BAF", headerText: "#fff" },
  { title: "The Creative Reel", desc: "Our first love — film and television. Long-form storytelling that elevates underrepresented voices and stories that deserve to be seen.", img: "https://img1.wsimg.com/isteam/getty/1428201539/:/cr=t:0%25,l:3.84%25,w:92.32%25,h:100%25/rs=w:500", headerBg: "#5170FF", headerText: "#fff" },
  { title: "The Creative Ensemble", desc: "A collaborative space for women who want to learn, grow, and share their creative journeys through events, workshops, and resources.", img: "https://img1.wsimg.com/isteam/getty/1137694152/:/cr=t:0%25,l:5.67%25,w:88.67%25,h:100%25/rs=w:500", headerBg: "#FFC2E8", headerText: "#000" },
  { title: "Ink + Print", desc: "We don't believe print is dead. Physical books and tangible relics that continue to inspire future generations.", img: "https://img1.wsimg.com/isteam/getty/1149290599/:/cr=t:0%25,l:12.57%25,w:74.86%25,h:100%25/rs=w:500", headerBg: "#000", headerText: "#FFC2E8" },
  { title: "Ensemble Branded", desc: "Partnering with brands we believe in to bring their narratives to life in the digital media landscape.", img: "https://img1.wsimg.com/isteam/getty/1828732247/:/cr=t:0%25,l:10.1%25,w:79.8%25,h:100%25/rs=w:500", headerBg: "#FC4BAF", headerText: "#000" },
];

const SERVICES = [
  { icon: Lightbulb, title: "Original IP", desc: "Creating original stories, from script to screen and podcast to post. Film, television, and digital content that elevates underrepresented voices.", bg: "#FC4BAF", text: "#fff" },
  { icon: Palette, title: "Branding + Marketing", desc: "We collaborate with creators and brands to shape ideas, support storytelling, and elevate each project through thoughtful branding and marketing.", bg: "#fff", text: "#FC4BAF" },
  { icon: Handshake, title: "Brand Partnerships", desc: "We love sharing the brands we believe in, partnering to bring their narratives to life and inspiring people to invest, engage, and share.", bg: "#FFC2E8", text: "#000" },
  { icon: Film, title: "Production", desc: "From script to screen, we offer hands-on multi-platform production across film, digital series, podcasts, and branded content.", bg: "#FC4BAF", text: "#fff" },
  { icon: Lightbulb, title: "Consulting", desc: "Strategic guidance on content development, distribution, and media strategy to ensure every story lands with clarity and impact.", bg: "#fff", text: "#FC4BAF" },
];

const TESTIMONIALS = [
  { quote: "Creative Ensemble completely transformed our brand presence. Their multi-platform approach brought our story to life in ways we never imagined.", author: "Jordan Mitchell", role: "Founder, Brightline Co." },
  { quote: "Working with CEM felt like having an in-house creative team. They understood our vision from day one and delivered beyond expectations.", author: "Aria Chen", role: "CEO, Nova Collective" },
  { quote: "The content they produce is exceptional. Every piece tells a story and connects with our audience on a deeper level.", author: "Marcus Rivera", role: "Brand Director, Solstice Media" },
];

/* ─── Wavy squiggle line decoration ─── */
const Squiggle = ({ color = "#FFC2E8", width = 200, strokeW = 8, className = "", style = {} }) => (
  <svg width={width} height={width * 0.6} viewBox="0 0 200 120" fill="none" className={className} style={style}>
    <path
      d="M10,60 C30,20 50,100 70,60 C90,20 110,100 130,60 C150,20 170,100 190,60"
      stroke={color}
      strokeWidth={strokeW}
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

/* ─── Organic blob shape ─── */
const Blob = ({ color = "#FC4BAF", size = 300, className = "", style = {} }) => {
  const src = color === "#000" ? "/flower-black.png" : color === "#5170FF" ? "/flower-blue.png" : "/flower-hotpink.png";
  return <FlowerImg src={src} size={size} className={className} style={style} />;
};

/* ─── Flower using actual image from user's Canva design ─── */
const FlowerImg = ({ src = "/flower-pink.png", size = 300, className = "", style = {} }) => (
  <img
    src={src}
    alt=""
    width={size}
    height={size}
    className={className}
    style={{ pointerEvents: "none", ...style }}
  />
);

const FlowerA = (props) => <FlowerImg {...props} />;
const FlowerB = (props) => <FlowerImg {...props} />;
const FlowerC = (props) => <FlowerImg {...props} />;

/* ─── Dot cluster decoration ─── */
const DotCluster = ({ color = "#5170FF", className = "", style = {} }) => (
  <div className={className} style={style}>
    {[...Array(16)].map((_, i) => (
      <div
        key={`dot-${i}`}
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
          margin: 3,
        }}
      />
    ))}
  </div>
);

/* ─── Animated section wrapper ─── */
const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const transition = useMemo(() => ({ duration: 0.65, delay, ease: "easeOut" }), [delay]);
  return (
    <motion.div
      ref={ref}
      initial={FADE_INITIAL}
      animate={inView ? FADE_VISIBLE : undefined}
      transition={transition}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ═══════════════════ KOHI-STYLE NAVBAR ═══════════════════ */
const NAV_LINKS = [
  { label: "Home", href: "#hero" },
  { label: "About", href: "#about" },
  { label: "The Ensemble", href: "#team" },
  { label: "Services", href: "#services" },
  { label: "Content", href: "#work" },
  { label: "Store", href: "#shop" },
  { label: "Contact", href: "#contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMenu = useCallback(() => setOpen(false), []);
  const openMenu = useCallback(() => setOpen(true), []);

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md border-b-3 border-black py-3"
            : "bg-transparent py-5"
        }`}
        style={{ borderBottom: scrolled ? "3px solid #000" : "none" }}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#hero"
            data-testid="navbar-logo"
            className="flex items-center gap-2"
          >
            <span
              style={{
                fontFamily: "Garet, sans-serif",
                fontWeight: 900,
                fontSize: "1.3rem",
                letterSpacing: "-0.03em",
                textTransform: "uppercase",
                lineHeight: 1,
              }}
            >
              <span style={{ color: "#FC4BAF" }}>Creative</span>
              <br />
              <span style={{ color: "#5170FF" }}>Ensemble</span>
              <br />
              <span style={{ color: "#000" }}>Media</span>
            </span>
          </a>

          {/* Right side: Book a Call + Hamburger */}
          <div className="flex items-center gap-4">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-btn"
              className="brutalist-btn hidden sm:inline-flex"
              style={{ padding: "10px 24px", fontSize: "0.8rem" }}
            >
              Book a Call
            </a>
            <button
              data-testid="nav-menu-btn"
              onClick={openMenu}
              className="w-12 h-12 border-3 border-black flex items-center justify-center hover:bg-[#FFC2E8] transition-colors"
              style={{ border: "3px solid #000" }}
            >
              <Menu size={22} strokeWidth={3} />
            </button>
          </div>
        </div>
      </nav>

      {/* Kohi-style sidebar overlay nav */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={OVERLAY_INITIAL}
              animate={OVERLAY_VISIBLE}
              exit={OVERLAY_EXIT}
              transition={OVERLAY_TRANSITION}
              className="sidebar-overlay"
              onClick={closeMenu}
            />
            <motion.div
              initial={SIDEBAR_INITIAL}
              animate={SIDEBAR_VISIBLE}
              exit={SIDEBAR_EXIT}
              transition={SIDEBAR_TRANSITION}
              className="sidebar-panel"
              data-testid="sidebar-nav"
            >
              <button
                data-testid="sidebar-close-btn"
                className="absolute top-8 right-8 w-12 h-12 border-3 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                style={{ border: "3px solid #000" }}
                onClick={closeMenu}
              >
                <X size={24} strokeWidth={3} />
              </button>

              {/* Decorative squiggle */}
              <Squiggle
                color="#FC4BAF"
                width={150}
                strokeW={6}
                style={{ position: "absolute", top: 30, left: 20, opacity: 0.3, transform: "rotate(-10deg)" }}
              />

              <div className="flex flex-col gap-4 relative z-10">
                {NAV_LINKS.map((l, i) => (
                  <motion.a
                    key={l.label}
                    href={l.href}
                    data-testid={`sidebar-nav-${l.label.toLowerCase()}`}
                    className="nav-link"
                    onClick={closeMenu}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.06, duration: 0.4 }}
                  >
                    {l.label}
                  </motion.a>
                ))}
              </div>

              <motion.a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sidebar-book-btn"
                className="brutalist-btn mt-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                Book a Call <ArrowRight size={18} />
              </motion.a>

              {/* Social icons at bottom */}
              <div className="flex gap-3 mt-auto pt-8">
                <a href="https://www.instagram.com/creativeensemblemedia" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                  <Instagram size={18} />
                </a>
                <a href="https://www.facebook.com/CreativeEnsembleMedia/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border-2 border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                  <Facebook size={18} />
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

/* ═══════════════════ HERO — SPLASHY ═══════════════════ */
const Hero = () => (
  <section
    id="hero"
    data-testid="hero-section"
    className="relative min-h-screen flex items-center overflow-hidden"
    style={{ background: "#FFC2E8" }}
  >
    {/* Flower — black on mobile for contrast against pink bg */}
    <FlowerImg
      src="/flower-white.png"
      size={500}
      className="block md:hidden"
      style={{ position: "absolute", top: "-10%", right: "-8%", opacity: 0.15, zIndex: 0 }}
    />
    <FlowerImg
      src="/flower-hotpink.png"
      size={500}
      className="hidden md:block"
      style={{ position: "absolute", top: "-10%", right: "-8%", opacity: 0.9, zIndex: 0 }}
    />
    <Squiggle
      color="#FFC2E8"
      width={280}
      strokeW={10}
      style={{ position: "absolute", bottom: "18%", left: "-2%", opacity: 0.7, transform: "rotate(-20deg)" }}
    />
    <Squiggle
      color="#fff"
      width={180}
      strokeW={6}
      style={{ position: "absolute", top: "8%", left: "3%", opacity: 0.4, transform: "rotate(10deg)" }}
    />
    {/* Dot cluster */}
    <DotCluster
      color="#5170FF"
      style={{ position: "absolute", top: "12%", right: "30%", width: 100, opacity: 0.6 }}
    />

    <div className="max-w-7xl mx-auto px-6 pt-32 pb-24 relative z-10 w-full">
      <div className="grid md:grid-cols-2 gap-10 items-center">
        <div>
          <FadeIn>
            <p
              className="uppercase tracking-[0.3em] text-xs font-semibold mb-6"
              style={{ color: "#FC4BAF", fontFamily: "Outfit, sans-serif" }}
            >
              Developing original stories, brands & creative collaborations.
            </p>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h1
              style={{
                fontFamily: "Garet, sans-serif",
                fontWeight: 900,
                fontSize: "clamp(3.2rem, 8vw, 7rem)",
                lineHeight: 0.9,
                letterSpacing: "-0.03em",
                textTransform: "uppercase",
                color: "#000",
                marginBottom: "2rem",
              }}
            >
              <span style={{ color: "#FC4BAF" }}>Creative</span>
              <br />
              <span style={{ color: "#5170FF" }}>Ensemble</span>
              <br />
              Media
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p
              className="text-lg leading-relaxed max-w-md mb-10"
              style={{ fontFamily: "Outfit, sans-serif", color: "#000" }}
            >
              Multi-platform content & production studio.
              <br />
              Building brands, art + ideas.
            </p>
          </FadeIn>

          <FadeIn delay={0.3}>
            <div className="flex flex-wrap gap-4">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-btn"
                className="brutalist-btn"
              >
                Book a Consultation <ArrowRight size={18} />
              </a>
              <a
                href="#work"
                data-testid="hero-work-btn"
                className="brutalist-btn"
                style={{
                  background: "#fff",
                  color: "#000",
                  boxShadow: "6px 6px 0px 0px #000",
                }}
              >
                View Our Content
              </a>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.3} className="hidden md:block">
          <div className="relative">
            <div
              style={{
                border: "3px solid #000",
                overflow: "hidden",
                boxShadow: "14px 14px 0px 0px #000",
              }}
            >
              <img
                src="/hero_mood_white2.png"
                alt="Creative Ensemble Studio"
                className="w-full h-auto object-cover"
                style={{ aspectRatio: "4/3" }}
              />
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ═══════════════════ MARQUEE ═══════════════════ */
const MarqueeSection = () => (
  <div className="marquee-section" data-testid="marquee-section">
    <Marquee speed={70} gradient={false}>
      {[...Array(5)].map((_, i) => (
        <span key={`marquee-${i}`} className="marquee-text">
          ORIGINAL IP &middot; IN-HOUSE BRANDS &middot; BRAND PARTNERSHIPS &middot; CONTENT PRODUCTION &middot; BUILDING BRANDS, ART + IDEAS &middot;
        </span>
      ))}
    </Marquee>
  </div>
);

/* ═══════════════════ ABOUT — SPLASHY ═══════════════════ */
const About = () => (
  <section
    id="about"
    data-testid="about-section"
    className="relative py-28 md:py-36 overflow-hidden"
    style={{ background: "#000", color: "#fff" }}
  >
    {/* Squiggles on dark bg */}
    <Squiggle color="#FC4BAF" width={300} strokeW={10} style={{ position: "absolute", top: "8%", right: "-5%", opacity: 0.4, transform: "rotate(15deg)" }} />
    <Squiggle color="#FFC2E8" width={220} strokeW={8} style={{ position: "absolute", bottom: "10%", left: "-3%", opacity: 0.3, transform: "rotate(-25deg)" }} />

    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="grid md:grid-cols-2 gap-16 items-center">
        <FadeIn>
          <p
            className="uppercase tracking-[0.3em] text-xs font-semibold mb-4"
            style={{ color: "#FFC2E8" }}
          >
            About the Studio
          </p>
          <h2
            style={{
              fontFamily: "Garet, sans-serif",
              fontWeight: 900,
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              lineHeight: 0.95,
              letterSpacing: "-0.02em",
              textTransform: "uppercase",
              marginBottom: "1.5rem",
            }}
          >
            Creating Content That
            <br />
            Connects — <span style={{ color: "#FC4BAF" }}>Together</span>
          </h2>
          <div
            className="border-l-4 pl-6 mb-8"
            style={{ borderColor: "#FC4BAF" }}
          >
            <p className="italic text-lg text-white/70 mb-1" style={{ fontFamily: "Outfit" }}>
              en&middot;sem&middot;ble{" "}
              <span className="text-sm not-italic text-white/40">/noun/</span>
            </p>
            <p className="text-white/60" style={{ fontFamily: "Outfit" }}>
              A group, such as a Creative Ensemble, producing a single effect.
            </p>
          </div>
          <p className="text-base leading-relaxed text-white mb-6" style={{ fontFamily: "Outfit" }}>
            We develop and produce story-driven content designed to connect people, ideas, and communities across platforms. As a multi-platform content studio, we create original podcasts, digital series, editorial work, branded campaigns, in-house brands, artwork, and creative collaborations that amplify new voices and underrepresented perspectives.
          </p>
        </FadeIn>

        <FadeIn delay={0.15}>
          <div className="grid grid-cols-2 gap-5">
            <div
              className="p-8 flex flex-col items-center justify-center text-center"
              style={{ background: "#FFC2E8", color: "#000", border: "3px solid #FFC2E8" }}
            >
              <span style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "3.5rem", lineHeight: 1, letterSpacing: "-0.03em" }}>
                24
              </span>
              <span className="text-sm uppercase tracking-wider mt-2 font-semibold" style={{ fontFamily: "Outfit" }}>
                Projects
              </span>
            </div>
            <div
              className="p-8 flex flex-col items-center justify-center text-center"
              style={{ background: "#5170FF", color: "#fff", border: "3px solid #5170FF" }}
            >
              <span style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "3.5rem", lineHeight: 1, letterSpacing: "-0.03em" }}>
                14
              </span>
              <span className="text-sm uppercase tracking-wider mt-2 font-semibold" style={{ fontFamily: "Outfit" }}>
                Brands
              </span>
            </div>
            <div
              className="p-8 flex flex-col items-center justify-center text-center col-span-2"
              style={{ background: "#FC4BAF", color: "#fff", border: "3px solid #FC4BAF" }}
            >
              <span style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "2.5rem", lineHeight: 1, letterSpacing: "-0.02em" }}>
                Multi-Platform
              </span>
              <span className="text-sm uppercase tracking-wider mt-2 font-semibold" style={{ fontFamily: "Outfit" }}>
                Content & Production
              </span>
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ═══════════════════ THE ENSEMBLE — TEAM ═══════════════════ */
const TEAM = [
  { name: "Rachel Owen", role: "Founder, Writer & Creative Director", desc: "18+ years in studio management and creative project leadership. Has worked with clients including Netflix, Showtime, HBO, OWN, and KTLA.", img: "https://img1.wsimg.com/isteam/ip/e596385a-18fe-45fe-a9d6-be8b4418ca7c/IMG_0002.jpeg/:/cr=t:0%25,l:0%25,w:100%25,h:100%25/rs=w:400,h:400,cg:true" },
  { name: "Shea Sinclair", role: "Director, Projects", desc: "17+ years in entertainment post-production. Expert in creative project leadership from preproduction through delivery.", img: "https://img1.wsimg.com/isteam/ip/e596385a-18fe-45fe-a9d6-be8b4418ca7c/lanternexecs11-7-18_shea_0010.jpeg/:/cr=t:0.1%25,l:0%25,w:95.52%25,h:63.66%25/rs=w:400,h:400,cg:true,m" },
  { name: "Carly Mo", role: "Director of Story & Strategy", desc: "A decade of digital content strategy and brand voice development. Worked with Wegovy, Mayo Clinic, TrueCar, ColourPop, and more.", img: "https://customer-assets.emergentagent.com/job_media-lab-17/artifacts/hq655e6z_IMG_4124.jpeg" },
  { name: "Vanessa Lipschutz", role: "Creative Strategist", desc: "Strategic operations leader and Co-Founder of Thing 7 AI. Specializes in AI-driven strategy and marketing automation.", img: "https://customer-assets.emergentagent.com/job_media-lab-17/artifacts/nhhiv0pu_image.png" },
  { name: "Chelsea Litoff", role: "Digital Artist / Creative Consultant", desc: "Digital artist specializing in visual storytelling and intentional design with bold patterns and vibrant colors.", img: "https://customer-assets.emergentagent.com/job_media-lab-17/artifacts/foouivb4_IMG_4125.jpeg" },
];

const Team = () => (
  <section id="team" data-testid="team-section" className="py-28 md:py-36 relative overflow-hidden" style={{ background: "#fff" }}>
    <FlowerImg src="/flower-pink.png" size={180} style={{ position: "absolute", bottom: "-3%", right: "-3%", opacity: 0.12, zIndex: 0 }} />

    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <FadeIn>
        <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-4 text-center" style={{ color: "#FC4BAF" }}>
          Who We Are
        </p>
        <h2 className="text-center mb-16" style={{ fontFamily: "Garet, sans-serif", fontWeight: 900, fontSize: "clamp(2.5rem, 5vw, 4rem)", textTransform: "uppercase", color: "#000", letterSpacing: "-0.02em" }}>
          The Ensemble
        </h2>
      </FadeIn>

      <div className="flex flex-wrap justify-center gap-6">
        {TEAM.map((t, i) => (
          <FadeIn key={t.name} delay={i * 0.08}>
            <div className="text-center w-40" data-testid={`team-member-${i}`}>
              <div className="w-32 h-32 mx-auto mb-4 overflow-hidden rounded-full" style={{ background: "#fff", border: "none", outline: "none", boxShadow: "none" }}>
                <img src={t.img} alt={t.name} className="w-full h-full object-cover" style={{ border: "none", outline: "none", transform: "scale(1.15)" }} />
              </div>
              <h3 className="text-sm uppercase mb-1" style={{ fontFamily: "Garet", fontWeight: 900, letterSpacing: "0.02em" }}>
                {t.name}
              </h3>
              <p className="text-xs uppercase tracking-wider mb-3" style={{ fontFamily: "Outfit", color: "#FC4BAF", fontWeight: 600 }}>
                {t.role}
              </p>
              <p className="text-xs leading-relaxed text-black/50" style={{ fontFamily: "Outfit" }}>
                {t.desc}
              </p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);


/* ═══════════════════ SERVICES — SPLASHY COLORED CARDS ═══════════════════ */
const ServicesSection = () => (
  <section
    id="services"
    data-testid="services-section"
    className="py-28 md:py-36 relative overflow-hidden"
    style={{ background: "#5170FF" }}
  >
    <Squiggle color="#FFC2E8" width={280} strokeW={10} style={{ position: "absolute", top: "5%", left: "-3%", opacity: 0.4, transform: "rotate(-15deg)" }} />
    <Squiggle color="#FFC2E8" width={200} strokeW={7} style={{ position: "absolute", bottom: "8%", right: "-2%", opacity: 0.35, transform: "rotate(20deg)" }} />
    <DotCluster color="#FFC2E8" style={{ position: "absolute", top: "10%", right: "5%", width: 100, opacity: 0.4 }} />

    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <FadeIn>
        <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-4 text-center" style={{ color: "#FFC2E8" }}>
          What We Do
        </p>
        <h2
          className="text-center mb-16"
          style={{
            fontFamily: "Garet, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(2.5rem, 5vw, 4rem)",
            textTransform: "uppercase",
            color: "#fff",
            letterSpacing: "-0.02em",
          }}
        >
          Our Services
        </h2>
      </FadeIn>

      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6" style={{ alignItems: "stretch" }}>
        {SERVICES.map((s, i) => (
            <FadeIn key={s.title} delay={i * 0.1} className="h-full">
              <div
                className="p-8 flex flex-col group cursor-pointer transition-transform duration-200 hover:-translate-y-2 h-full"
                data-testid={`service-card-${i}`}
                style={{
                  background: s.bg,
                  border: "3px solid #000",
                  boxShadow: "8px 8px 0px 0px #000",
                  minHeight: 300,
                  color: s.text,
                }}
              >
                <div
                  className="w-14 h-14 flex items-center justify-center mb-6"
                  style={{
                    border: `3px solid ${s.text}`,
                    color: s.text,
                  }}
                >
                  <s.icon size={26} />
                </div>
                <h3
                  className="text-xl mb-3 uppercase"
                  style={{ fontFamily: "Garet", fontWeight: 900, letterSpacing: "-0.01em" }}
                >
                  {s.title}
                </h3>
                <p className="text-sm leading-relaxed flex-1 opacity-90" style={{ fontFamily: "Outfit" }}>
                  {s.desc}
                </p>
              </div>
            </FadeIn>
          ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════ WORK / CONTENT ═══════════════════ */
const Work = () => (
  <section id="work" data-testid="work-section" className="py-28 md:py-36 relative overflow-hidden" style={{ background: "#fff" }}>
    <FlowerImg src="/flower-pink.png" size={200} style={{ position: "absolute", top: "5%", right: "-4%", opacity: 0.15, zIndex: 0 }} />

    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <FadeIn>
        <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-4 text-center" style={{ color: "#FC4BAF" }}>
          What We Create
        </p>
        <h2
          className="text-center mb-16"
          style={{ fontFamily: "Garet, sans-serif", fontWeight: 900, fontSize: "clamp(2.5rem, 5vw, 4rem)", textTransform: "uppercase", color: "#000", letterSpacing: "-0.02em" }}
        >
          Our Work
        </h2>
      </FadeIn>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PORTFOLIO.map((p, i) => {
          const isEnsemble = p.title === "The Creative Ensemble";
          const Wrapper = isEnsemble ? "a" : "div";
          const wrapperProps = isEnsemble
            ? { href: "https://thecreativeensemble.com", target: "_blank", rel: "noopener noreferrer" }
            : {};
          return (
            <FadeIn key={p.title} delay={i * 0.08}>
              <Wrapper
                {...wrapperProps}
                data-testid={`portfolio-item-${i}`}
                className="transition-transform duration-200 hover:-translate-y-1 h-full flex flex-col"
                style={{ border: "3px solid #000", overflow: "hidden", background: "#000", textDecoration: "none" }}
              >
                <div className="p-5" style={{ background: p.headerBg }}>
                  <h3 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.1rem", textTransform: "uppercase", color: p.headerText, letterSpacing: "0.02em" }}>
                    {p.title}
                  </h3>
                </div>
                <div style={{ height: 180, overflow: "hidden" }}>
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex-1 flex flex-col" style={{ background: "#000" }}>
                  <p className="text-sm leading-relaxed flex-1" style={{ fontFamily: "Outfit", color: "#fff" }}>
                    {p.desc}
                  </p>
                  {isEnsemble && (
                    <div className="mt-4">
                      <span className="brutalist-btn brutalist-btn-pink text-center w-full" style={{ fontSize: "0.8rem", padding: "10px 20px" }}>
                        Join Now <ArrowRight size={14} className="inline ml-1" />
                      </span>
                    </div>
                  )}
                </div>
              </Wrapper>
            </FadeIn>
          );
        })}
      </div>
    </div>
  </section>
);

/* ═══════════════════ PROCESS — PINK BACKGROUND ═══════════════════ */
const Process = () => {
  const steps = [
    { num: "01", title: "Discover", desc: "We start with your story. Whether it's an original IP, a brand partnership, or a creative collaboration, we dig into your vision, audience, and goals." },
    { num: "02", title: "Create", desc: "From script to screen, podcast to post, print to digital — our ensemble brings ideas to life across every platform with thoughtful production and design." },
    { num: "03", title: "Amplify", desc: "We produce, publish, and distribute your content — managing branding, marketing, and partnerships to ensure every story lands with clarity and impact." },
  ];

  return (
    <section
      id="process"
      data-testid="process-section"
      className="py-28 md:py-36 relative overflow-hidden"
      style={{ background: "#FFC2E8" }}
    >
      <Squiggle color="#FC4BAF" width={260} strokeW={10} style={{ position: "absolute", bottom: "8%", right: "-3%", opacity: 0.4, transform: "rotate(25deg)" }} />
    <FlowerImg src="/flower-black.png" size={200} style={{ position: "absolute", top: "-5%", left: "-3%", opacity: 0.1, zIndex: 0 }} />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <FadeIn>
          <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-4 text-center" style={{ color: "#000" }}>
            How We Work
          </p>
          <h2
            className="text-center mb-20"
            style={{
              fontFamily: "Garet, sans-serif",
              fontWeight: 900,
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              textTransform: "uppercase",
              color: "#000",
              letterSpacing: "-0.02em",
            }}
          >
            The Process
          </h2>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <FadeIn key={s.num} delay={i * 0.15}>
              <div
                className="relative p-10"
                data-testid={`process-step-${i}`}
                style={{ background: "#000", color: "#fff", border: "3px solid #000" }}
              >
                <span className="process-number absolute -top-4 -left-2" style={{ color: "#5170FF", opacity: 1 }}>
                  {s.num}
                </span>
                <div className="relative z-10 pt-14">
                  <h3
                    className="text-2xl uppercase mb-4"
                    style={{ fontFamily: "Garet", fontWeight: 900, letterSpacing: "-0.01em" }}
                  >
                    {s.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-white/60" style={{ fontFamily: "Outfit" }}>
                    {s.desc}
                  </p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════ TESTIMONIALS — BLUE BACKGROUND ═══════════════════ */
const Testimonials = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const len = TESTIMONIALS.length;
    const timer = setInterval(() => setCurrent((c) => (c + 1) % len), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      data-testid="testimonials-section"
      className="py-28 md:py-36 relative overflow-hidden"
      style={{ background: "#5170FF" }}
    >
      <Squiggle color="#FFC2E8" width={280} strokeW={10} style={{ position: "absolute", top: "8%", left: "-3%", opacity: 0.35, transform: "rotate(-10deg)" }} />
      <Squiggle color="#FFC2E8" width={200} strokeW={7} style={{ position: "absolute", bottom: "12%", right: "-2%", opacity: 0.3, transform: "rotate(30deg)" }} />

      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        <FadeIn>
          <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-4" style={{ color: "#FFC2E8" }}>
            Client Love
          </p>
          <h2
            className="mb-16"
            style={{
              fontFamily: "Garet, sans-serif",
              fontWeight: 900,
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              textTransform: "uppercase",
              color: "#fff",
              letterSpacing: "-0.02em",
            }}
          >
            Happy Clients
          </h2>
        </FadeIn>

        <div className="min-h-[220px] flex items-center justify-center">
          <motion.div
            key={current}
            initial={TESTIMONIAL_INITIAL}
            animate={TESTIMONIAL_VISIBLE}
            exit={TESTIMONIAL_INITIAL}
            transition={TESTIMONIAL_TRANSITION}
          >
            <p className="testimonial-quote mb-8" style={{ color: "#fff" }} data-testid="testimonial-quote">
              &ldquo;{TESTIMONIALS[current].quote}&rdquo;
            </p>
            <p className="font-bold text-sm uppercase tracking-wider" style={{ fontFamily: "Outfit", color: "#FFC2E8" }} data-testid="testimonial-author">
              {TESTIMONIALS[current].author}
            </p>
            <p className="text-sm mt-1" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.5)" }}>
              {TESTIMONIALS[current].role}
            </p>
          </motion.div>
        </div>

        <div className="flex justify-center gap-3 mt-10">
          {TESTIMONIALS.map((t, i) => (
            <button
              key={t.author}
              data-testid={`testimonial-dot-${i}`}
              onClick={() => setCurrent(i)}
              className="w-4 h-4 transition-colors duration-200"
              style={{
                border: "2px solid #fff",
                background: i === current ? "#FFC2E8" : "transparent",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════ STORE — COMING SOON ═══════════════════ */
const Shop = () => (
  <section id="shop" data-testid="shop-section" className="py-28 md:py-36 relative overflow-hidden" style={{ background: "#000" }}>
    <Squiggle color="#FC4BAF" width={250} strokeW={9} style={{ position: "absolute", top: "5%", right: "-3%", opacity: 0.2, transform: "rotate(-15deg)" }} />
    <FlowerImg src="/flower-hotpink.png" size={200} style={{ position: "absolute", bottom: "-5%", left: "-3%", opacity: 0.15, zIndex: 0 }} />

    <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
      <FadeIn>
        <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-4" style={{ color: "#FC4BAF" }}>
          Coming Soon
        </p>
        <h2
          className="mb-8"
          style={{
            fontFamily: "Garet, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(2.5rem, 5vw, 4rem)",
            textTransform: "uppercase",
            color: "#fff",
            letterSpacing: "-0.02em",
          }}
        >
          Ink & Print <span style={{ color: "#FFC2E8" }}>Store</span>
        </h2>
        <p className="text-lg text-white/60 max-w-xl mx-auto mb-10" style={{ fontFamily: "Outfit" }}>
          We don't believe print is dead. We still get goosebumps in bookstores and find inspiration in the scent of vintage pages. Our Ink & Print Store is coming soon with curated goods, prints, and more.
        </p>
        <div className="inline-flex gap-4">
          <a href="#contact" data-testid="store-notify-btn" className="brutalist-btn brutalist-btn-pink">
            Notify Me <ArrowRight size={18} />
          </a>
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ═══════════════════ SECOND MARQUEE (before contact) ═══════════════════ */
const MarqueeBlue = () => (
  <div
    data-testid="marquee-blue"
    style={{
      background: "#5170FF",
      padding: "16px 0",
      borderTop: "3px solid #000",
      borderBottom: "3px solid #000",
    }}
  >
    <Marquee speed={50} direction="right" gradient={false}>
      {[...Array(5)].map((_, i) => (
        <span
          key={`blue-marquee-${i}`}
          style={{
            fontFamily: "Garet, sans-serif",
            fontWeight: 900,
            fontSize: "1.3rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "#fff",
            whiteSpace: "nowrap",
            padding: "0 2rem",
          }}
        >
          WE DEVELOP & PRODUCE CONTENT DESIGNED TO CONNECT &bull;&nbsp;
        </span>
      ))}
    </Marquee>
  </div>
);


/* ═══════════════════ BLOG / NEWS — COMING SOON ═══════════════════ */
const Blog = () => (
  <section id="blog" data-testid="blog-section" className="py-28 md:py-36 relative overflow-hidden" style={{ background: "#fff" }}>
    <Squiggle color="#FFC2E8" width={220} strokeW={8} style={{ position: "absolute", top: "10%", right: "-2%", opacity: 0.3, transform: "rotate(15deg)" }} />

    <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
      <FadeIn>
        <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-4" style={{ color: "#FC4BAF" }}>
          Coming Soon
        </p>
        <h2 className="mb-8" style={{ fontFamily: "Garet, sans-serif", fontWeight: 900, fontSize: "clamp(2.5rem, 5vw, 4rem)", textTransform: "uppercase", color: "#000", letterSpacing: "-0.02em" }}>
          Updates & <span style={{ color: "#5170FF" }}>News</span>
        </h2>
        <p className="text-lg text-black/60 max-w-xl mx-auto mb-10" style={{ fontFamily: "Outfit" }}>
          Stay tuned for behind-the-scenes looks, project updates, industry insights, and stories from The Ensemble. Sign up below to be the first to know.
        </p>
        <a href="#contact" data-testid="blog-notify-btn" className="brutalist-btn brutalist-btn-blue">
          Join the Mailing List <ArrowRight size={18} />
        </a>
      </FadeIn>
    </div>
  </section>
);

/* ═══════════════════ CONTACT FORM ═══════════════════ */
const ContactForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("");

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(EMPTY_FORM);
    } catch {
      setStatus("error");
    }
  }, [form]);

  const updateField = useCallback((field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }, []);

  return (
    <div className="p-8 md:p-10" style={{ background: "#fff", border: "3px solid #000", boxShadow: "10px 10px 0px 0px #000" }}>
      <h3 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.3rem", textTransform: "uppercase", marginBottom: "1.5rem" }}>
        Send a Message
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input data-testid="contact-name" type="text" placeholder="Your Name" className="brutalist-input" value={form.name} onChange={updateField("name")} />
        <input data-testid="contact-email" type="email" placeholder="Your Email" className="brutalist-input" value={form.email} onChange={updateField("email")} />
        <textarea data-testid="contact-message" placeholder="Your Message" rows={4} className="brutalist-input resize-none" value={form.message} onChange={updateField("message")} />
        <button data-testid="contact-submit-btn" type="submit" className="brutalist-btn w-full">
          <Send size={16} /> Send Message
        </button>
        {status === "success" && (
          <p data-testid="contact-success" className="text-sm font-semibold text-center mt-2" style={{ color: "#5170FF" }}>
            Message sent! We&rsquo;ll be in touch.
          </p>
        )}
        {status === "error" && (
          <p data-testid="contact-error" className="text-sm font-semibold text-center mt-2 text-red-600">
            Something went wrong. Please try again.
          </p>
        )}
      </form>
      <NewsletterForm />
    </div>
  );
};

/* ═══════════════════ NEWSLETTER FORM ═══════════════════ */
const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/newsletter`, { email });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  }, [email]);

  return (
    <div className="mt-10 pt-8" style={{ borderTop: "3px solid #000" }}>
      <h4 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.1rem", textTransform: "uppercase", marginBottom: "1rem" }}>
        <Mail size={18} className="inline mr-2" />
        Join the Mailing List
      </h4>
      <form onSubmit={handleSubmit} className="flex gap-3">
        <input data-testid="newsletter-email" type="email" placeholder="your@email.com" className="brutalist-input flex-1" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="newsletter-submit-btn" type="submit" className="brutalist-btn brutalist-btn-blue whitespace-nowrap" style={{ padding: "14px 22px" }}>
          <ArrowRight size={18} />
        </button>
      </form>
      {status === "success" && (
        <p data-testid="newsletter-success" className="text-sm font-semibold mt-3" style={{ color: "#5170FF" }}>
          You&rsquo;re subscribed!
        </p>
      )}
      {status === "error" && (
        <p data-testid="newsletter-error" className="text-sm font-semibold mt-3 text-red-600">
          Couldn&rsquo;t subscribe. Try again.
        </p>
      )}
    </div>
  );
};

/* ═══════════════════ CALENDLY PANEL ═══════════════════ */
const CalendlyPanel = () => (
  <div className="overflow-hidden h-full flex flex-col" style={{ background: "#fff", border: "3px solid #000", boxShadow: "10px 10px 0px 0px #5170FF" }}>
    <div className="p-6 flex items-center justify-between" style={{ borderBottom: "3px solid #000" }}>
      <h3 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.3rem", textTransform: "uppercase" }}>
        Book a Call
      </h3>
      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="calendly-external-link"
        className="flex items-center gap-1 text-sm font-semibold"
        style={{ color: "#5170FF", fontFamily: "Outfit" }}
      >
        Open in Calendly <ExternalLink size={14} />
      </a>
    </div>
    <div className="flex-1 min-h-[500px]">
      <iframe
        data-testid="calendly-embed"
        src={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=ffffff&text_color=000000&primary_color=5170FF`}
        width="100%"
        height="100%"
        frameBorder="0"
        title="Schedule a consultation"
        style={{ minHeight: 500 }}
      />
    </div>
  </div>
);

/* ═══════════════════ CONTACT SECTION ═══════════════════ */
const Contact = () => (
  <section id="contact" data-testid="contact-section" className="py-28 md:py-36 relative overflow-hidden" style={{ background: "#FFC2E8" }}>
    <Squiggle color="#FC4BAF" width={300} strokeW={10} style={{ position: "absolute", bottom: "8%", left: "-3%", opacity: 0.3, transform: "rotate(15deg)" }} />
    <FlowerImg src="/flower-black.png" size={180} style={{ position: "absolute", top: "5%", right: "-3%", opacity: 0.06, zIndex: 0 }} />

    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <FadeIn>
        <p className="uppercase tracking-[0.3em] text-xs font-semibold mb-4 text-center" style={{ color: "#000" }}>
          Get in Touch
        </p>
        <h2
          className="text-center mb-16"
          style={{
            fontFamily: "Garet, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(2.5rem, 5vw, 4rem)",
            textTransform: "uppercase",
            color: "#000",
            letterSpacing: "-0.02em",
          }}
        >
          Let&rsquo;s Connect
        </h2>
      </FadeIn>

      <div className="grid lg:grid-cols-2 gap-10">
        <FadeIn>
          <ContactForm />
        </FadeIn>
        <FadeIn delay={0.15}>
          <CalendlyPanel />
        </FadeIn>
      </div>
    </div>
  </section>
);

/* ═══════════════════ FOOTER ═══════════════════ */
const Footer = () => (
  <footer data-testid="footer-section" className="py-24 md:py-32 relative overflow-hidden" style={{ background: "#000", color: "#fff" }}>
    <Squiggle color="#FFC2E8" width={250} strokeW={8} style={{ position: "absolute", top: "10%", right: "-2%", opacity: 0.15, transform: "rotate(20deg)" }} />

    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="grid md:grid-cols-2 gap-12 items-end mb-16">
        <div>
          <h2
            style={{
              fontFamily: "Garet, sans-serif",
              fontWeight: 900,
              fontSize: "clamp(3rem, 6vw, 5.5rem)",
              lineHeight: 0.9,
              letterSpacing: "-0.03em",
              textTransform: "uppercase",
              marginBottom: "2rem",
            }}
          >
            Let&rsquo;s
            <br />
            <span style={{ color: "#FFC2E8" }}>Talk.</span>
          </h2>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-book-btn"
            className="brutalist-btn brutalist-btn-pink inline-flex"
          >
            Book a Consultation <ArrowRight size={18} />
          </a>
        </div>

        <div className="md:text-right">
          <p style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.2rem", textTransform: "uppercase", letterSpacing: "-0.01em", marginBottom: "1rem" }}>
            <span style={{ color: "#fff" }}>Creative</span>
            <span style={{ color: "#5170FF" }}>Ensemble</span>
            <span style={{ color: "#FFC2E8" }}>Media</span>
          </p>
          <p className="text-sm text-white/40 mb-6" style={{ fontFamily: "Outfit" }}>
            Multi-platform content & production studio.
            <br />
            Building brands, art + ideas.
          </p>
          <div className="flex gap-4 md:justify-end">
            <a href="https://www.instagram.com/creativeensemblemedia" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
              className="w-12 h-12 flex items-center justify-center hover:text-black transition-all duration-200"
              style={{ border: "3px solid rgba(255,255,255,0.3)" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC2E8"; e.currentTarget.style.borderColor = "#FFC2E8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; }}
            >
              <Instagram size={20} />
            </a>
            <a href="https://www.facebook.com/CreativeEnsembleMedia/" target="_blank" rel="noopener noreferrer" data-testid="footer-facebook"
              className="w-12 h-12 flex items-center justify-center transition-all duration-200"
              style={{ border: "3px solid rgba(255,255,255,0.3)" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#5170FF"; e.currentTarget.style.borderColor = "#5170FF"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; }}
            >
              <Facebook size={20} />
            </a>
            <a href="mailto:rachel@creativeensemblemedia.com" data-testid="footer-email"
              className="w-12 h-12 flex items-center justify-center transition-all duration-200"
              style={{ border: "3px solid rgba(255,255,255,0.3)" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#fff"; e.currentTarget.style.color = "#000"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; e.currentTarget.style.color = "#fff"; }}
            >
              <Mail size={20} />
            </a>
          </div>
        </div>
      </div>

      <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <p className="text-xs text-white/30" style={{ fontFamily: "Outfit" }}>
          &copy; {new Date().getFullYear()} Creative Ensemble Media. All Rights Reserved.
        </p>
        <div className="flex gap-6">
          {["About", "Services", "Content", "Store", "Contact"].map((l) => (
            <a key={l} href={`#${l.toLowerCase()}`} className="text-xs text-white/30 hover:text-white transition-colors uppercase tracking-wider" style={{ fontFamily: "Outfit" }}>
              {l}
            </a>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

/* ═══════════════════ APP WITH ROUTING ═══════════════════ */
import { HubAuth, HubPage } from "./Hub";

const BACKEND_URL_FULL = process.env.REACT_APP_BACKEND_URL;

const HubRoute = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL_FULL}/api/auth/me`, { withCredentials: true })
      .then(({ data }) => { setUser(data); setChecking(false); })
      .catch(() => { setUser(null); setChecking(false); });
  }, []);

  const handleLogout = useCallback(async () => {
    await axios.post(`${BACKEND_URL_FULL}/api/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  }, []);

  if (checking) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#FFC2E8" }}><p style={{ fontFamily: "Garet", fontWeight: 900 }}>Loading...</p></div>;
  if (!user) return <HubAuth onAuth={setUser} />;
  return <HubPage user={user} onLogout={handleLogout} />;
};

const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.25 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to top"
          data-testid="scroll-to-top"
          className="fixed bottom-8 right-8 z-[60] w-14 h-14 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          style={{ background: "#FC4BAF", border: "3px solid #000", color: "#fff", boxShadow: "6px 6px 0px 0px #000" }}
        >
          <ArrowUp size={22} strokeWidth={3} />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

const HomePage = () => (
  <div className="App">
    <Navbar />
    <Hero />
    <MarqueeSection />
    <About />
    <Team />
    <ServicesSection />
    <Work />
    <Process />
    <Shop />
    <MarqueeBlue />
    <Blog />
    <Contact />
    <Footer />
    <ScrollToTop />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/hub" element={<HubRoute />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
